import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import Button from '../components/Button';
import BracketView from '../components/BracketView';
import FormField, { Input } from '../components/FormField';
import MatchModal from '../components/MatchModal';
import Tooltip from '../components/Tooltip';
import TeamHoverName from '../components/TeamHoverName';
import { FORMAT_DESCRIPTIONS } from '../utils/tournamentFormats';
import { AvatarWithFallback } from '../components/Avatar';

function fmt(d) {
  if (!d) return '–';
  return new Date(d).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const FORMAT_LABELS = {
  single_elimination: 'Single Elimination',
  double_elimination: 'Double Elimination',
  ffa_scoreboard: 'FFA-Scoreboard',
  seeded_single_elimination: 'Seeded Single Elimination',
  bingo: 'Bingo',
  battle_royale: 'Battle Royale',
  time_trial: 'Zeitfahren',
};

const STATUS_CFG = {
  open:        { label: 'Anmeldung offen', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  in_progress: { label: 'Läuft',           cls: 'bg-ocean-50 text-ocean-700 border-ocean-200' },
  finished:    { label: 'Beendet',         cls: 'bg-slate-100 text-slate-500 border-slate-200' },
  cancelled:   { label: 'Abgesagt',        cls: 'bg-red-50 text-red-600 border-red-200' },
};

function TeamCard({ team, tournament, onAction }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const confirmed = (team.members || []).filter((m) => m.status === 'confirmed');
  const pending = (team.members || []).filter((m) => m.status === 'pending');
  const isCreator = user && team.creator_id === user.id;
  const isMember = user && confirmed.some((m) => m.user_id === user.id);
  const hasPending = user && pending.some((m) => m.user_id === user.id);
  const isFull = team.is_full;

  return (
    <div className={`card border-l-4 ${isFull ? 'border-l-emerald-400' : 'border-l-ocean-400'}`}>
      <div className="flex justify-between items-start mb-3">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <Link to={`/teams/${team.id}`} className="font-bold text-ocean-900 hover:underline">{team.name}</Link>
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold border ${
              isFull ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
              'bg-slate-50 text-slate-500 border-slate-200'
            }`}>
              {confirmed.length}/{tournament.team_size} Spieler{isFull ? ' – Voll' : ''}
            </span>
          </div>
          <p className="text-xs text-slate-400 mt-0.5">
            Captain: {(() => {
              const c = team.members?.find((m) => m.role === 'creator');
              const label = c?.user?.nickname || c?.user?.username;
              return c && label
                ? <Link to={`/users/${c.user_id}`} className="hover:underline text-ocean-600">{label}</Link>
                : '–';
            })()}
          </p>
        </div>
        <div className="flex gap-2">
          {isCreator ? (
            <Button size="sm" variant="secondary" onClick={() => navigate(`/teams/${team.id}`)}>
              Verwalten
            </Button>
          ) : isMember ? (
            <span className="text-xs text-emerald-600 font-semibold bg-emerald-50 px-2 py-1 rounded border border-emerald-200">
              Dein Team
            </span>
          ) : hasPending ? (
            <span className="text-xs text-amber-600 font-semibold bg-amber-50 px-2 py-1 rounded border border-amber-200">
              Anfrage ausstehend
            </span>
          ) : !isFull && user && onAction ? (
            <Button size="sm" onClick={() => onAction('join', team.id)}>Beitreten</Button>
          ) : null}
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {confirmed.map((m) => (
          <Link key={m.id} to={`/users/${m.user_id}`}
            className="text-xs bg-slate-100 text-slate-700 hover:bg-ocean-50 hover:text-ocean-700 px-2 py-1 rounded-full flex items-center gap-1.5 no-underline transition-colors">
            {m.role === 'creator' && <span className="text-[10px] text-ocean-500">★</span>}
            <AvatarWithFallback user={m.user} size="xs" />
            {m.user?.nickname || m.user?.username}
          </Link>
        ))}
        {Array.from({ length: Math.max(0, tournament.team_size - confirmed.length) }).map((_, i) => (
          <span key={i} className="text-xs bg-slate-50 text-slate-300 border border-dashed border-slate-200 px-2 py-1 rounded-full">
            Offen
          </span>
        ))}
      </div>
    </div>
  );
}

const PUBLIC_STATUS = {
  upcoming:  { label: 'Demnächst',      cls: 'bg-slate-100 text-slate-500' },
  open:      { label: 'Läuft gerade',   cls: 'bg-amber-100 text-amber-700' },
  completed: { label: 'Abgeschlossen',  cls: 'bg-emerald-100 text-emerald-700' },
};

function PublicRoundCard({ round, label, tournamentStatus }) {
  const badge = round.status === 'open' && tournamentStatus === 'open'
    ? { label: 'Offen', cls: 'bg-slate-100 text-slate-500' }
    : PUBLIC_STATUS[round.status] || PUBLIC_STATUS.upcoming;
  return (
    <div className={`card mb-2 ${round.status === 'open' ? 'border border-amber-200' : ''}`}>
      <div className="flex items-center gap-2 mb-2">
        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">{label}</p>
        <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${badge.cls}`}>{badge.label}</span>
        {round.note && (
          <span className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded">🗺 {round.note}</span>
        )}
      </div>
      {round.status === 'upcoming' ? (
        <p className="text-xs text-slate-300 text-center py-3">Noch nicht gestartet</p>
      ) : round.scores.length === 0 ? (
        <p className="text-xs text-slate-300 text-center py-3">Keine Ergebnisse</p>
      ) : (
        <table className="w-full text-xs">
          <thead>
            <tr className="text-slate-400 border-b border-slate-100">
              <th className="text-left py-1 font-medium">Platz</th>
              <th className="text-left py-1 font-medium">Spieler</th>
              <th className="text-right py-1 font-medium">Frags</th>
              <th className="text-right py-1 font-medium">Punkte</th>
            </tr>
          </thead>
          <tbody>
            {round.scores.map((s) => (
              <tr key={s.team_id} className="border-b border-slate-50">
                <td className="py-1 font-bold text-slate-600">#{s.placement}</td>
                <td className="py-1 text-slate-700"><TeamHoverName teamId={s.team_id} name={s.team_name} /></td>
                <td className="py-1 text-right text-slate-600">{s.frags}</td>
                <td className="py-1 text-right font-bold text-ocean-600">{s.points}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default function TournamentDetail() {
  const { id } = useParams();
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [tournament, setTournament] = useState(null);
  const [bracket, setBracket] = useState(null);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ffaData, setFfaData] = useState(null);
  const [brData, setBrData] = useState(null);
  const [bingoParticipants, setBingoParticipants] = useState(null);
  const [hasTicket, setHasTicket] = useState(false);
  const [activeTab, setActiveTab] = useState('bracket');
  const [teamName, setTeamName] = useState('');
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [onWaitlist, setOnWaitlist] = useState(false);
  const [waitlistLoading, setWaitlistLoading] = useState(false);

  const load = useCallback(() => {
    Promise.all([
      api.get(`/api/tournaments/${id}`),
      api.get(`/api/tournaments/${id}/bracket`),
    ]).then(([tRes, bRes]) => {
      setTournament(tRes.data);
      setBracket(bRes.data);
      setTeams(bRes.data.teams || []);
      if (tRes.data.format === 'bingo' || tRes.data.format === 'time_trial') setActiveTab('info');
      if (tRes.data.format === 'ffa_scoreboard') {
        api.get(`/api/tournaments/${id}/ffa`).then((r) => setFfaData(r.data)).catch(() => {});
      }
      if (tRes.data.format === 'battle_royale') {
        api.get(`/api/tournaments/${id}/battle-royale`).then((r) => setBrData(r.data)).catch(() => {});
      }
      if (tRes.data.format === 'bingo') {
        api.get(`/api/events/${tRes.data.event_id}/participants?paid_only=true`).then((r) => setBingoParticipants(r.data)).catch(() => setBingoParticipants([]));
      }
    }).catch(() => navigate('/'))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const eventId = tournament?.event_id;
  useEffect(() => {
    if (!user || !eventId) { setHasTicket(false); return; }
    api.get('/api/tickets/my')
      .then((r) => {
        const found = r.data.find((t) => t.event_id === eventId && t.status !== 'cancelled');
        setHasTicket(!!found);
      })
      .catch(() => setHasTicket(false));
  }, [user, eventId]);

  const myTeam = user && teams.find((t) =>
    (t.members || []).some((m) => m.user_id === user.id && (m.status === 'confirmed' || m.status === 'pending'))
  );

  const isAdminAssigned = tournament?.team_assignment_mode === 'admin_assigned';

  useEffect(() => {
    if (!user || !tournament || !isAdminAssigned) { setOnWaitlist(false); return; }
    api.get(`/api/tournaments/${id}/my-waitlist-status`)
      .then((r) => setOnWaitlist(r.data.on_waitlist))
      .catch(() => setOnWaitlist(false));
  }, [user, tournament, isAdminAssigned, id]);

  const joinWaitlist = async () => {
    setWaitlistLoading(true); setError(''); setMessage('');
    try {
      await api.post(`/api/tournaments/${id}/waitlist`);
      setOnWaitlist(true);
      setMessage('Erfolgreich angemeldet! Der Admin teilt dich in Kürze einem Team zu.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler');
    } finally { setWaitlistLoading(false); }
  };

  const leaveWaitlist = async () => {
    setWaitlistLoading(true); setError(''); setMessage('');
    try {
      await api.delete(`/api/tournaments/${id}/waitlist`);
      setOnWaitlist(false);
      setMessage('Anmeldung zurückgezogen.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler');
    } finally { setWaitlistLoading(false); }
  };

  const handleAction = async (action, teamId) => {
    setError(''); setMessage('');
    try {
      if (action === 'join') {
        await api.post(`/api/teams/${teamId}/join`);
        setMessage('Beitrittsanfrage gesendet – warte auf Bestätigung des Team-Creators.');
      }
      load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler');
    }
  };

  const createTeam = async (e) => {
    e.preventDefault();
    if (!teamName.trim()) return;
    setCreating(true); setError('');
    try {
      const res = await api.post(`/api/tournaments/${id}/teams`, { name: teamName.trim() });
      setShowCreate(false); setTeamName('');
      setMessage('Team erstellt!');
      navigate(`/teams/${res.data.id}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler');
    } finally { setCreating(false); }
  };

  const registerSolo = async () => {
    setCreating(true); setError('');
    try {
      const res = await api.post(`/api/tournaments/${id}/teams`, { name: user.username });
      setMessage('Erfolgreich angemeldet!');
      navigate(`/teams/${res.data.id}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler');
    } finally { setCreating(false); }
  };

  if (loading) return (
    <div className="flex justify-center py-16">
      <div className="animate-spin rounded-full h-8 w-8 border-2 border-ocean-400 border-t-transparent" />
    </div>
  );

  if (!tournament) return null;

  const cfg = STATUS_CFG[tournament.status] || STATUS_CFG.open;
  const canRegister = user && !myTeam && !onWaitlist && tournament.status === 'open' && !tournament.bracket_generated;
  const isInfoOnly = ['bingo', 'time_trial'].includes(tournament.format);
  const isTimeTrial = tournament.format === 'time_trial';

  return (
    <div className="max-w-5xl mx-auto py-6 px-4">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-3 flex-wrap mb-1">
              <h1 className="text-2xl font-bold text-ocean-900">{tournament.name}</h1>
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${cfg.cls}`}>{cfg.label}</span>
            </div>
            <div className="flex flex-wrap gap-4 text-sm text-slate-500">
              {tournament.game && <span className="font-medium text-ocean-700">{tournament.game}</span>}
              <span className="inline-flex items-center gap-1.5">
                {FORMAT_LABELS[tournament.format]}
                <Tooltip text={FORMAT_DESCRIPTIONS[tournament.format]} />
              </span>
              {tournament.team_size > 1 && <span>{tournament.team_size}v{tournament.team_size}</span>}
              {tournament.third_place_match && !isInfoOnly && <span>inkl. Spiel um Platz 3</span>}
              {tournament.start_time && <span>Start: {fmt(tournament.start_time)}</span>}
            </div>
          </div>
          {!isInfoOnly && (
            <div className="text-center bg-white border border-slate-200 rounded-xl px-4 py-2 shadow-sm">
              <div className="text-2xl font-bold text-ocean-700">
                {tournament.teams_full} / {tournament.max_teams || '∞'}
              </div>
              <div className="text-xs text-slate-400">Teams qualifiziert</div>
            </div>
          )}
          {tournament.format === 'bingo' && (
            <div className="text-center bg-white border border-slate-200 rounded-xl px-4 py-2 shadow-sm">
              <div className="text-2xl font-bold text-ocean-700">
                {bingoParticipants?.length ?? '…'}
              </div>
              <div className="text-xs text-slate-400">Teilnehmer</div>
            </div>
          )}
        </div>
      </div>

      {/* Messages */}
      {message && <div className="alert-success mb-4">{message}</div>}
      {error && <div className="alert-error mb-4">{error}</div>}

      {/* Register CTA */}
      {!isInfoOnly && canRegister && !showCreate && (
        <div className="bg-gradient-to-r from-ocean-50 to-reef-50 border border-ocean-200 rounded-xl p-4 mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="font-semibold text-ocean-800">Du bist noch nicht angemeldet</p>
            <p className="text-xs text-ocean-600 mt-0.5">
              {isAdminAssigned
                ? 'Melde dich an – der Admin teilt dich anschließend einem Team zu.'
                : tournament.team_size === 1
                ? `Du wirst als „${user.username}" angemeldet`
                : `Erstelle ein Team (${tournament.team_size} Spieler) oder trete einem bei`}
            </p>
          </div>
          {isAdminAssigned ? (
            <Button onClick={joinWaitlist} loading={waitlistLoading}>Anmelden</Button>
          ) : tournament.team_size === 1 ? (
            <Button onClick={registerSolo} loading={creating}>Anmelden</Button>
          ) : (
            <Button onClick={() => setShowCreate(true)}>Team erstellen</Button>
          )}
        </div>
      )}

      {/* Warteliste: bereits angemeldet, wartet auf Zuteilung */}
      {!isInfoOnly && isAdminAssigned && onWaitlist && !myTeam && (
        <div className="bg-gradient-to-r from-amber-50 to-amber-100/50 border border-amber-200 rounded-xl p-4 mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="font-semibold text-amber-800">Du bist angemeldet</p>
            <p className="text-xs text-amber-700 mt-0.5">
              Der Admin teilt dich in Kürze einem Team zu. Du wirst hier automatisch angezeigt, sobald es soweit ist.
            </p>
          </div>
          {tournament.status === 'open' && !tournament.bracket_generated && (
            <Button variant="ghost" onClick={leaveWaitlist} loading={waitlistLoading}>Anmeldung zurückziehen</Button>
          )}
        </div>
      )}

      {/* Create team form (only for multi-player tournaments, self_organized only) */}
      {!isInfoOnly && !isAdminAssigned && showCreate && tournament.team_size > 1 && (
        <div className="card border border-ocean-200 mb-6">
          <h3 className="section-title mb-3">Neues Team erstellen</h3>
          <form onSubmit={createTeam} className="flex gap-3 flex-wrap items-end">
            <FormField label="Team-Name" className="flex-1 min-w-[200px]">
              <Input
                value={teamName}
                onChange={(e) => setTeamName(e.target.value)}
                placeholder="z.B. Die Besten..."
                required
              />
            </FormField>
            <div className="flex gap-2 pb-1">
              <Button type="submit" loading={creating}>Erstellen</Button>
              <Button variant="ghost" type="button" onClick={() => { setShowCreate(false); setError(''); }}>Abbrechen</Button>
            </div>
          </form>
        </div>
      )}

      {!isInfoOnly && !user && tournament.status === 'open' && (
        <div className="alert-info mb-6">
          <span>Einloggen um an diesem Turnier teilzunehmen.</span>
          <button className="ml-2 underline font-semibold" onClick={() => navigate('/login')}>Jetzt einloggen</button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200 mb-6">
        {(tournament.format === 'bingo'
          ? [
              { key: 'info', label: 'Info' },
              { key: 'teams', label: (isAdmin || hasTicket) ? `Teilnehmer (${bingoParticipants?.length ?? '…'})` : 'Teilnehmer' },
            ]
          : isTimeTrial
          ? [{ key: 'info', label: 'Info' }]
          : tournament.format === 'ffa_scoreboard'
          ? [
              { key: 'bracket', label: 'Scoreboard' },
              { key: 'teams', label: `Spieler (${teams.length})` },
              { key: 'info', label: 'Info' },
            ]
          : tournament.format === 'battle_royale'
          ? [
              { key: 'bracket', label: 'Scoreboard' },
              { key: 'teams', label: `Teams (${teams.length})` },
              { key: 'info', label: 'Info' },
            ]
          : [
              { key: 'bracket', label: 'Turnierbaum' },
              { key: 'teams', label: `Teams (${teams.length})` },
              { key: 'info', label: 'Info' },
            ]
        ).map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.key
                ? 'border-ocean-500 text-ocean-700'
                : 'border-transparent text-slate-500 hover:text-ocean-600'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* FFA Scoreboard */}
      {activeTab === 'bracket' && tournament.format === 'ffa_scoreboard' && (
        <div className="space-y-6">
          {!ffaData ? (
            <div className="card text-center py-10 text-slate-400">Lade...</div>
          ) : (
            <>
              {/* Phase 1: Qualifikation */}
              <div className="rounded-xl border border-ocean-200 overflow-hidden">
                <div className="bg-ocean-600 px-4 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-white font-bold text-sm">Phase 1 — Qualifikation</span>
                  </div>
                  <span className="text-ocean-200 text-xs">
                    {ffaData.quali_rounds.filter(r => r.status === 'completed').length} / {ffaData.ffa_quali_rounds} Runden
                  </span>
                </div>
                <div className="p-4 space-y-3 bg-white">
                  {ffaData.quali_rounds.map((r) => (
                    <PublicRoundCard key={r.id} round={r} label={`Qualifikation Runde ${r.round_number}`} tournamentStatus={tournament.status} />
                  ))}

                  {ffaData.quali_standings.length > 0 && (
                    <div className="card mt-1">
                      <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Qualifikations-Stand</p>
                      {(() => {
                        const finalistIds = ffaData.ffa_final_generated
                          ? new Set((ffaData.finalists || []).map(f => f.team_id))
                          : null;
                        return (
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-xs text-slate-400 border-b border-slate-100">
                                <th className="text-left py-1 pr-3 font-medium">#</th>
                                <th className="text-left py-1 font-medium">Spieler</th>
                                <th className="text-right py-1 font-medium">Punkte</th>
                                <th className="text-right py-1 font-medium">Frags</th>
                              </tr>
                            </thead>
                            <tbody>
                              {ffaData.quali_standings.map((s) => {
                                const inFinal = finalistIds ? finalistIds.has(s.team_id) : null;
                                return (
                                  <tr key={s.team_id} className="border-b border-slate-50">
                                    <td className="py-2 pr-3 font-bold text-ocean-700">{s.rank}</td>
                                    <td className={`py-2 font-medium ${inFinal === false ? 'text-slate-400' : 'text-slate-800'}`}>
                                      <TeamHoverName teamId={s.team_id} name={s.team_name} />
                                      {inFinal && <span className="ml-2 text-[10px] text-ocean-400 font-semibold">→ Finale</span>}
                                    </td>
                                    <td className="py-2 text-right font-bold text-sand-600">{s.total_points}</td>
                                    <td className="py-2 text-right text-slate-500">{s.total_frags}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        );
                      })()}
                    </div>
                  )}
                </div>
              </div>

              {/* Phase 2: Finale */}
              {ffaData.ffa_final_generated && (
                <div className="rounded-xl border border-sand-300 overflow-hidden">
                  <div className="bg-sand-500 px-4 py-3 flex items-center justify-between">
                    <span className="text-white font-bold text-sm">Phase 2 — Finale</span>
                    <span className="text-sand-100 text-xs">Top {ffaData.finalists?.length || 8} Spieler</span>
                  </div>
                  <div className="p-4 space-y-3 bg-white">
                    {ffaData.finalists && ffaData.finalists.length > 0 && !ffaData.final_round?.scores?.length && (
                      <div className="card bg-slate-50">
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Finalisten</p>
                        <div className="flex flex-wrap gap-2">
                          {ffaData.finalists.map((s) => (
                            <span key={s.team_id} className="text-xs px-2 py-1 rounded-full bg-ocean-50 text-ocean-700 border border-ocean-200 font-semibold">
                              {s.rank}. <TeamHoverName teamId={s.team_id} name={s.team_name} />
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {ffaData.final_round && (
                      <>
                        {ffaData.final_standings.length > 0 && (
                          <div className="card">
                            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Finale-Ergebnis</p>
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="text-xs text-slate-400 border-b border-slate-100">
                                  <th className="text-left py-1 pr-3 font-medium">#</th>
                                  <th className="text-left py-1 font-medium">Spieler</th>
                                  <th className="text-right py-1 font-medium">Punkte</th>
                                  <th className="text-right py-1 font-medium">Frags</th>
                                </tr>
                              </thead>
                              <tbody>
                                {ffaData.final_standings.map((s) => (
                                  <tr key={s.team_id} className="border-b border-slate-50">
                                    <td className="py-2 pr-3 font-bold text-lg">
                                      {s.rank === 1 ? '🥇' : s.rank === 2 ? '🥈' : s.rank === 3 ? '🥉' : s.rank}
                                    </td>
                                    <td className="py-2 font-medium text-slate-800"><TeamHoverName teamId={s.team_id} name={s.team_name} /></td>
                                    <td className="py-2 text-right font-bold text-sand-600">{s.total_points}</td>
                                    <td className="py-2 text-right text-slate-500">{s.total_frags}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                        <PublicRoundCard round={ffaData.final_round} label="Finale" tournamentStatus={tournament.status} />
                      </>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Battle Royale Scoreboard */}
      {activeTab === 'bracket' && tournament.format === 'battle_royale' && (
        <div className="space-y-6">
          {!brData ? (
            <div className="card text-center py-10 text-slate-400">Lade...</div>
          ) : (
            <>
              {brData.rounds.length === 0 && (
                <div className="card text-center py-8 text-slate-400 text-sm">
                  Noch keine Runden gespielt.
                </div>
              )}

              <div className="space-y-3">
                {brData.rounds.map((r) => (
                  <div key={r.id} className="card mb-2">
                    <div className="flex items-center gap-2 mb-2">
                      <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">
                        Runde {r.round_number}
                      </p>
                      <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${
                        r.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                      }`}>
                        {r.status === 'completed' ? 'Abgeschlossen' : 'Offen'}
                      </span>
                      {r.map_name && (
                        <span className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded">🗺 {r.map_name}</span>
                      )}
                    </div>
                    {r.scores.length === 0 ? (
                      <p className="text-xs text-slate-300 text-center py-3">Keine Ergebnisse</p>
                    ) : (
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-slate-400 border-b border-slate-100">
                            <th className="text-left py-1 font-medium">Platz</th>
                            <th className="text-left py-1 font-medium">Team</th>
                            <th className="text-right py-1 font-medium">Kills</th>
                            <th className="text-right py-1 font-medium">Punkte</th>
                          </tr>
                        </thead>
                        <tbody>
                          {r.scores.map((s) => (
                            <tr key={s.team_id} className="border-b border-slate-50">
                              <td className="py-1 font-bold text-slate-600">#{s.placement}</td>
                              <td className="py-1 text-slate-700"><TeamHoverName teamId={s.team_id} name={s.team_name} /></td>
                              <td className="py-1 text-right text-slate-600">{s.kills}</td>
                              <td className="py-1 text-right font-bold text-ocean-600">{s.points}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                ))}
              </div>

              {brData.standings.length > 0 && (
                <div className="card mt-1">
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Gesamtwertung</p>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-slate-400 border-b border-slate-100">
                        <th className="text-left py-1 pr-3 font-medium">#</th>
                        <th className="text-left py-1 font-medium">Team</th>
                        <th className="text-right py-1 font-medium">Punkte</th>
                        <th className="text-right py-1 font-medium">Kills</th>
                      </tr>
                    </thead>
                    <tbody>
                      {brData.standings.map((s) => (
                        <tr key={s.team_id} className="border-b border-slate-50">
                          <td className="py-2 pr-3 font-bold text-lg">
                            {s.rank === 1 ? '🥇' : s.rank === 2 ? '🥈' : s.rank === 3 ? '🥉' : s.rank}
                          </td>
                          <td className="py-2 font-medium text-slate-800"><TeamHoverName teamId={s.team_id} name={s.team_name} /></td>
                          <td className="py-2 text-right font-bold text-sand-600">{s.total_points}</td>
                          <td className="py-2 text-right text-slate-500">{s.total_kills}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Standard Bracket */}
      {activeTab === 'bracket' && tournament.format !== 'ffa_scoreboard' && tournament.format !== 'battle_royale' && (
        <BracketView
          tournament={tournament}
          matches={bracket?.matches || []}
          teams={bracket?.teams || teams}
          roundNotes={bracket?.round_notes || []}
          onMatchClick={(match) => setSelectedMatch(match)}
        />
      )}

      {selectedMatch && (() => {
        const winnerRounds = (bracket?.matches || [])
          .filter((m) => m.bracket_side === 'winners')
          .map((m) => m.round);
        const finalRound = winnerRounds.length > 0 ? Math.max(...winnerRounds) : 0;
        return (
          <MatchModal
            match={selectedMatch}
            user={user}
            isAdmin={['admin', 'superadmin', 'tournament_admin'].includes(user?.role)}
            finalRound={finalRound}
            onClose={() => setSelectedMatch(null)}
            onRefresh={() => { load(); setSelectedMatch(null); }}
          />
        );
      })()}

      {activeTab === 'teams' && tournament.format === 'bingo' && (
        <div className="card">
          <p className="section-title mb-4">Teilnehmer</p>
          {!(isAdmin || hasTicket) ? (
            <div className="text-center py-10 text-slate-400">
              <div className="text-3xl mb-3">🔒</div>
              <p className="font-medium text-slate-600 mb-1">Nur für Teilnehmer sichtbar</p>
              <p className="text-sm">Buche ein Ticket für dieses Event, um die Teilnehmerliste zu sehen.</p>
            </div>
          ) : bingoParticipants === null ? (
            <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-6 w-6 border-2 border-ocean-400 border-t-transparent" /></div>
          ) : bingoParticipants.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-6">Noch keine Teilnehmer.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-slate-400 uppercase tracking-wider border-b border-slate-100">
                  <th className="text-left py-2 pr-4 font-medium w-8">#</th>
                  <th className="text-left py-2 pr-4 font-medium">Nickname</th>
                  <th className="text-left py-2 font-medium">Sitzplatz</th>
                </tr>
              </thead>
              <tbody>
                {bingoParticipants.map((p, i) => (
                  <tr key={i} className="border-b border-slate-50 hover:bg-slate-50">
                    <td className="py-2.5 pr-4 text-slate-400 font-medium">{i + 1}</td>
                    <td className="py-2.5 pr-4 font-semibold text-slate-800">{p.nickname}</td>
                    <td className="py-2.5 text-slate-500">{p.seat_label || '–'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {activeTab === 'teams' && tournament.format !== 'bingo' && (
        !(isAdmin || hasTicket) ? (
          <div className="card text-center py-10 text-slate-400">
            <div className="text-3xl mb-3">🔒</div>
            <p className="font-medium text-slate-600 mb-1">Nur für Teilnehmer sichtbar</p>
            <p className="text-sm">Buche ein Ticket für dieses Event, um die Spielerübersicht zu sehen.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {teams.length === 0 ? (
              <div className="card text-center py-10 text-slate-400">
                <div className="text-3xl mb-2">👥</div>
                <p>Noch keine Teams angemeldet.</p>
              </div>
            ) : (
              teams.map((team) => (
                <TeamCard
                  key={team.id}
                  team={team}
                  tournament={tournament}
                  onAction={!myTeam && !isAdminAssigned ? handleAction : null}
                />
              ))
            )}
          </div>
        )
      )}

      {activeTab === 'info' && (
        <div className="card">
          <dl className="space-y-3 text-sm">
            <div className="flex justify-between border-b border-slate-100 pb-3">
              <dt className="text-slate-500 font-medium">Format</dt>
              <dd className="font-semibold text-slate-800 inline-flex items-center gap-1.5">
                {FORMAT_LABELS[tournament.format]}
                <Tooltip text={FORMAT_DESCRIPTIONS[tournament.format]} />
              </dd>
            </div>
            {!isInfoOnly && (
              <div className="flex justify-between border-b border-slate-100 pb-3">
                <dt className="text-slate-500 font-medium">Teamgröße</dt>
                <dd className="font-semibold text-slate-800">{tournament.team_size} Spieler</dd>
              </div>
            )}
            {!isInfoOnly && tournament.max_teams && (
              <div className="flex justify-between border-b border-slate-100 pb-3">
                <dt className="text-slate-500 font-medium">Max. Teams</dt>
                <dd className="font-semibold text-slate-800">{tournament.max_teams}</dd>
              </div>
            )}
            {!isInfoOnly && (
              <div className="flex justify-between border-b border-slate-100 pb-3">
                <dt className="text-slate-500 font-medium">Spiel um Platz 3</dt>
                <dd className="font-semibold text-slate-800">{tournament.third_place_match ? 'Ja' : 'Nein'}</dd>
              </div>
            )}
            {tournament.format === 'bingo' && (
              <div className="flex justify-between border-b border-slate-100 pb-3">
                <dt className="text-slate-500 font-medium">Teilnahme</dt>
                <dd className="font-semibold text-slate-800">Automatisch für alle Ticket-Inhaber</dd>
              </div>
            )}
            {tournament.start_time && (
              <div className="flex justify-between border-b border-slate-100 pb-3">
                <dt className="text-slate-500 font-medium">Startzeit</dt>
                <dd className="font-semibold text-slate-800">{fmt(tournament.start_time)}</dd>
              </div>
            )}
            {tournament.description && (
              <div className={tournament.rules ? "border-b border-slate-100 pb-3" : ""}>
                <dt className="text-slate-500 font-medium mb-1">Beschreibung</dt>
                <dd className="text-slate-700 leading-relaxed whitespace-pre-wrap">{tournament.description}</dd>
              </div>
            )}
            {tournament.rules && (
              <div>
                <dt className="text-slate-500 font-medium mb-1">Spielregeln/Servereinstellungen</dt>
                <dd className="text-slate-700 leading-relaxed whitespace-pre-wrap">{tournament.rules}</dd>
              </div>
            )}
          </dl>
        </div>
      )}
    </div>
  );
}
